/*
 * lavila-lens · v1 — the storefront behaviour capture of lavila-bi.
 * Catalogue: lavila-bi/docs/MARKETING_EVENT_CATALOGUE_V1.md (1.0, amended);
 * behaviour: MARKETING_BEHAVIOR_SPEC.md §4–§5 with ruling 2 amended
 * (2026-09-11): the visitor id is Shopify's own `_shopify_y` cookie, read
 * only under consent and never written; the session id is `lavila_sid`;
 * batches go straight to lavila-bi (no edge).
 *
 * Plain ES2017, no build step. Loaded with data-cookieconsent="ignore" so it
 * runs in every consent state and BUFFERS: nothing is sent, read or written
 * until Cookiebot answers; a decline sends the anonymous subset only; a page
 * closed without an answer sends that subset anonymously and drops the rest.
 * The pure parts are exported for node tests (scripts/lens).
 *
 * Naming rule (catalogue §5): none of the blocker words appears in this
 * file's own names, strings or comments; the two catalogue field names that
 * contain one, and the browser's send API, are assembled at runtime.
 */
(function (root) {
  "use strict";

  // ── constants ──────────────────────────────────────────────────────────
  // 2 (2026-09-15): the session cut depends on this load's own tagging, not on
  // every page load. Rides every event under K_TV (see emit), denied path included.
  var VERSION = 2;
  var K_TV = "trac" + "ker_version";
  var K_COLL = "col" + "lection_handle";
  var COLL_PATH = "/col" + "lections/";
  var ARR_COLL = "col" + "lection"; // the arrived_from value of the catalogue
  var SEND = "send" + "Beacon";
  var FLUSH_MS = 5000;
  var FLUSH_N = 20;
  var MAX_EVENTS = 50;
  var MAX_BYTES = 60000;
  var QUEUE_MAX = 200;
  var IDLE_MS = 30 * 60000;
  var CAP_MS = 6 * 3600000;
  var SID_COOKIE = "lavila_sid";
  var VID_COOKIE = "_shopify_y";
  var GA_COOKIE = "_ga";
  var ATTR_COOKIE = "lavila_attr";
  var LS_CLICKS = "lv_lens_clicks";
  var LS_SEEN = "lv_lens_seen";
  var SS_Q = "lv_lens_q";
  var SS_INTENT = "lv_lens_i";
  var DENIED_EVENTS = { page_view: 1, product_impression: 1, product_click: 1, card_swipe: 1 };
  var POSITION_EVENTS = { product_impression: 1, product_click: 1, card_swipe: 1 };
  var SEARCH_HOSTS = ["google.", "bing.", "duckduckgo.", "yahoo.", "ecosia.", "qwant."];
  var SOCIAL = ["instagram", "facebook", "pinterest", "tiktok", "youtube", "linkedin"];
  var CLICK_IDS = ["gclid", "fbclid", "epik", "ttclid", "msclkid", "li_fat_id"];

  // ── pure helpers ───────────────────────────────────────────────────────
  function uuid() {
    try {
      if (root.crypto && root.crypto.randomUUID) return root.crypto.randomUUID();
    } catch (e) {}
    var b = new Array(16);
    for (var i = 0; i < 16; i++) b[i] = (Math.random() * 256) | 0;
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = "";
    for (var j = 0; j < 16; j++) h += (b[j] + 256).toString(16).slice(1);
    return h.slice(0, 8) + "-" + h.slice(8, 12) + "-" + h.slice(12, 16) + "-" + h.slice(16, 20) + "-" + h.slice(20);
  }

  function hash36(s) {
    var h = 5381;
    for (var i = s.length; i; ) h = (h * 33) ^ s.charCodeAt(--i);
    return (h >>> 0).toString(36);
  }

  function readCookie(jar, name) {
    var m = String(jar || "").match(new RegExp("(?:^|;\\s*)" + name.replace(/[.$]/g, "\\$&") + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }

  /** GA's `_ga` is GA1.1.<cid>; the client id is everything after the second dot. */
  function gaClientId(raw) {
    if (!raw) return null;
    var p = raw.split(".");
    return p.length >= 4 ? p.slice(2).join(".") : raw;
  }

  function deviceClass(w) {
    return w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop";
  }
  function bucket100(n) {
    return Math.max(0, Math.floor(n / 100) * 100);
  }
  function pathOf(href) {
    try {
      return new URL(href, "https://lavilahome.com").pathname;
    } catch (e) {
      return String(href || "").split("?")[0];
    }
  }
  function hostOf(href) {
    try {
      return href ? new URL(href).hostname : "";
    } catch (e) {
      return "";
    }
  }
  function isSelf(host) {
    return /(^|\.)lavilahome\.com$|(^|\.)myshopify\.com$/i.test(host || "");
  }
  function cap(s, n) {
    s = s == null ? "" : String(s);
    return s.length > n ? s.slice(0, n) : s;
  }

  /** page_view source / medium / campaign and click-id presence (catalogue §2.1). */
  function classify(search, referrer) {
    var q = params(search);
    var tagPairs = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"]
      .concat(CLICK_IDS)
      .map(function (k) {
        return q[k] ? k + "=" + q[k] : "";
      });
    var refHost = hostOf(referrer);
    var ext = refHost && !isSelf(refHost) ? refHost.toLowerCase().replace(/^www\./, "") : "";
    var source = q.utm_source || "";
    var medium = q.utm_medium || "";
    var has = {};
    CLICK_IDS.forEach(function (k) {
      has[k] = !!q[k];
    });
    if (!source) {
      if (ext) {
        var cls = "";
        for (var i = 0; i < SEARCH_HOSTS.length; i++) if (ext.indexOf(SEARCH_HOSTS[i]) === 0) cls = SEARCH_HOSTS[i].replace(".", "");
        if (!cls) for (var j = 0; j < SOCIAL.length; j++) if (ext.indexOf(SOCIAL[j]) !== -1) cls = SOCIAL[j];
        if (!cls && ext.indexOf("klaviyo") !== -1) cls = "klaviyo";
        source = cls || ext;
      } else {
        source = "direct";
      }
    }
    if (!medium) {
      if (has.gclid || has.msclkid) medium = "cpc";
      else if (has.fbclid || has.epik || has.ttclid || has.li_fat_id) medium = "paid_social";
      else if (!ext) medium = "none";
      else if (source === "klaviyo") medium = "email";
      else if (["google", "bing", "duckduckgo", "yahoo", "ecosia", "qwant"].indexOf(source) !== -1) medium = "organic";
      else medium = "referral";
    }
    return {
      source: cap(source, 120),
      medium: cap(medium, 60),
      campaign: q.utm_campaign ? cap(q.utm_campaign, 256) : null,
      has_gclid: has.gclid,
      has_fbclid: has.fbclid,
      has_epik: has.epik,
      referrer_host: ext || null,
      /**
       * Does THIS page load carry campaign tagging of its own? Tested on the tag
       * SET, before hashing, because the hash of an untagged load is not falsy:
       * the twelve empty entries join to "&&&&&&&&&&&", which hashes to "4z5p37"
       * (and hash36("") is "45h"). A falsiness test on the key would silently
       * treat every untagged page as tagged.
       */
      tagged: tagPairs.some(Boolean),
      /** The key a session is cut on: any utm or click id value. */
      touchKey: hash36(tagPairs.join("&")),
    };
  }

  function params(search) {
    var out = {};
    String(search || "")
      .replace(/^\?/, "")
      .split("&")
      .forEach(function (kv) {
        if (!kv) return;
        var i = kv.indexOf("=");
        var k = decodeURIComponent((i < 0 ? kv : kv.slice(0, i)).replace(/\+/g, " "));
        var v = i < 0 ? "" : decodeURIComponent(kv.slice(i + 1).replace(/\+/g, " "));
        if (!(k in out)) out[k] = v;
      });
    return out;
  }

  /** The grid page's sort and filters from the URL (catalogue §1). */
  function gridState(search, sortDefault) {
    var q = params(search);
    var filters = {};
    var active = false;
    for (var k in q) if (k.indexOf("filter.") === 0) {
      active = true;
      filters[k] = cap(q[k], 200);
    }
    return { sort: q.sort_by || sortDefault || null, filters_active: active, filters: active ? filters : null };
  }

  /**
   * lavila_sid (spec §5): `id.start.last.touch`. A session continues while the
   * gap is under 30 min and the span under 6 h; a page load that carries its OWN
   * campaign tagging also cuts it when that tagging differs from the one the
   * session was born with. Pure: the caller reads and writes the cookie.
   *
   * The fourth argument used to be `isLanding`, and both callers passed `true`
   * unconditionally, so the touch comparison ran on EVERY page load. An untagged
   * internal page hashes to a key that differs from any tagged session's, so the
   * first click away from a `?gclid=…` landing minted a new session — and
   * classify() then labelled that replacement `direct`/`none`, moving paid,
   * social and email sessions to Directo (1.032 of 1.651 within-visit pairs over
   * 12 to 14 Sep 2026; lavila-bi plan v5.2 rulings 26 and 31). The cut now
   * depends on whether THIS load is tagged at all, which is the thing the rule
   * was always trying to express.
   */
  function sessionNext(current, now, touchKey, isTagged) {
    var p = current ? String(current).split(".") : [];
    var id = p[0], start = +p[1], last = +p[2], touch = p[3];
    var fresh = !id || !(start > 0) || !(last > 0) || now - last > IDLE_MS || now - start > CAP_MS || (isTagged && touch !== touchKey);
    if (fresh) return { id: uuid(), start: now, last: now, touch: touchKey, isNew: true };
    return { id: id, start: start, last: now, touch: touch, isNew: false };
  }
  function sessionValue(s) {
    return s.id + "." + s.start + "." + s.last + "." + s.touch;
  }

  /** search_norm_v1 (catalogue §2.10): the guard, then the normalisation. */
  function guard(text) {
    var s = String(text || "");
    s = s.replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "[pii:email]");
    s = s.replace(/\b[A-Z]{2}\d{2}(?:[ \-]?[A-Z0-9]){11,30}\b/g, "[pii:iban]");
    s = s.replace(/#\s?\d{4,}/g, "[pii:order]");
    s = s.replace(/\bLVA\d+\b/gi, "[pii:order]");
    s = s.replace(/(?:\+?\d[\d \-.()]{5,}\d)(?=\D|$)/g, function (m) {
      return m.replace(/\D/g, "").length >= 7 ? "[pii:phone]" : m;
    });
    s = s.replace(/\d{6,}/g, "[pii:number]");
    return cap(s, 120);
  }
  function normQuery(text) {
    var s = guard(text).toLowerCase();
    try {
      s = s.normalize("NFD").replace(/[̀-ͯ]/g, "");
    } catch (e) {}
    s = s.replace(/\s+/g, " ").trim().replace(/[.,;:!?¡¿\-]+$/g, "").trim();
    return cap(s, 120);
  }

  /**
   * The denied path (catalogue §3): which events exist and which fields
   * survive. Returns the anonymous event, or null when the event is not sent
   * under denial.
   */
  var DENIED_CORE = ["event_id", "event_name", "ts", "page_load_id", K_TV, "consent", "consent_state", "customer_state", "page_type", "device_class", "viewport_w", "viewport_h"];
  var DENIED_POSITION = [K_COLL, "product_handle", "grid_cols", "sort", "filters_active", "position_index", "position_cell", "position_span", "position_row", "position_col", "tile_variant", "page_number", "card_image_index", "dwell_ms", "new_tab", "target", "from_index", "to_index", "method"];
  var DENIED_PV = ["source", "medium", "campaign", "landing"];
  function denied(ev) {
    if (!DENIED_EVENTS[ev.event_name]) return null;
    if (ev.event_name === "page_view" && ev.landing !== true) return null;
    var keep = DENIED_CORE.concat(ev.event_name === "page_view" ? DENIED_PV : DENIED_POSITION);
    var out = {};
    keep.forEach(function (k) {
      if (ev[k] !== undefined) out[k] = ev[k];
    });
    out.consent = "denied";
    out.viewport_w = bucket100(ev.viewport_w);
    out.viewport_h = bucket100(ev.viewport_h);
    out.filters_active = !!ev.filters_active;
    return out;
  }

  /** Split a list of events into batches under both caps. */
  function batches(events) {
    var out = [];
    var cur = [];
    var size = 2;
    events.forEach(function (e) {
      var s = JSON.stringify(e).length + 1;
      if (cur.length >= MAX_EVENTS || size + s > MAX_BYTES) {
        if (cur.length) out.push(cur);
        cur = [];
        size = 2;
      }
      cur.push(e);
      size += s;
    });
    if (cur.length) out.push(cur);
    return out;
  }

  /** The buffered-consent queue: drop the oldest non-position event first past the cap. */
  function enqueue(queue, ev) {
    if (queue.length >= QUEUE_MAX) {
      var idx = -1;
      for (var i = 0; i < queue.length; i++) if (!POSITION_EVENTS[queue[i].event_name]) {
        idx = i;
        break;
      }
      queue.splice(idx < 0 ? 0 : idx, 1);
    }
    queue.push(ev);
    return queue;
  }

  var pure = {
    VERSION: VERSION,
    uuid: uuid,
    hash36: hash36,
    readCookie: readCookie,
    gaClientId: gaClientId,
    deviceClass: deviceClass,
    bucket100: bucket100,
    classify: classify,
    gridState: gridState,
    sessionNext: sessionNext,
    sessionValue: sessionValue,
    guard: guard,
    normQuery: normQuery,
    denied: denied,
    batches: batches,
    enqueue: enqueue,
    IDLE_MS: IDLE_MS,
    CAP_MS: CAP_MS,
    K_COLL: K_COLL,
    K_TV: K_TV,
  };
  if (typeof module !== "undefined" && module.exports) module.exports = pure;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.__lavilaLens) return;
  window.__lavilaLens = pure;

  // ── runtime ────────────────────────────────────────────────────────────
  var cfg;
  try {
    cfg = JSON.parse(document.getElementById("lavila-lens-config").textContent);
  } catch (e) {
    return;
  }
  if (!cfg || !cfg.url) return;

  var pageLoadId = uuid();
  var t0 = Date.now();
  var consent = "unknown"; // unknown | granted | denied
  var queue = []; // captured while unknown
  var buffer = []; // granted, awaiting a flush
  var timer = null;
  var session = null;
  var pdp = { views: 0, gallery: 0, variants: 0, scroll: 0, depths: {}, images: {}, accordions: {} };
  var lastPointer = { t: 0, el: null };
  var landingInfo = classify(location.search, document.referrer);
  var grid = gridState(location.search, cfg.sort_default);
  var attrKey = null;
  var seenImpressions = {};
  var live = {}; // impressions still on screen: event_id → { ev, since }

  // ── cookies and storage (granted only) ─────────────────────────────────
  function cookie(name) {
    return readCookie(document.cookie, name);
  }
  function writeSession(s) {
    try {
      document.cookie = SID_COOKIE + "=" + sessionValue(s) + "; path=/; max-age=" + Math.floor(IDLE_MS / 1000) + "; SameSite=Lax; Secure";
    } catch (e) {}
  }
  function touchSession() {
    if (consent !== "granted") return null;
    // No caller decides this any more: whether the load is tagged is a property
    // of the URL, and both callers were passing `true` for it.
    session = sessionNext(cookie(SID_COOKIE), Date.now(), landingInfo.touchKey, landingInfo.tagged);
    writeSession(session);
    return session;
  }
  function ls(key, value) {
    try {
      if (consent !== "granted") return null;
      if (value === undefined) {
        var raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      }
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
    return null;
  }
  function ss(key, value) {
    try {
      if (consent !== "granted") return null;
      if (value === undefined) {
        var raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      }
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {}
    return null;
  }
  function sha256(text) {
    try {
      return crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)).then(function (buf) {
        var a = new Uint8Array(buf), h = "";
        for (var i = 0; i < a.length; i++) h += (a[i] + 256).toString(16).slice(1);
        return h;
      });
    } catch (e) {
      return Promise.resolve(null);
    }
  }
  function computeAttrKey() {
    if (attrKey !== null) return;
    var raw = cookie(ATTR_COOKIE);
    if (!raw) return;
    try {
      var first = JSON.parse(raw).first;
      if (first) sha256(JSON.stringify(first)).then(function (h) { attrKey = h; });
    } catch (e) {}
  }

  // ── envelope ───────────────────────────────────────────────────────────
  function gridCols() {
    var c = document.getElementById("ProductGridContainer");
    if (!c) return null;
    if (window.innerWidth < 768) return +(c.getAttribute("data-lv-cols-mobile") || 2);
    var v = parseInt(getComputedStyle(c).getPropertyValue("--current-column"), 10);
    return v > 0 ? v : null;
  }
  function envelope(name, fields, state) {
    var ev = {
      event_id: uuid(),
      event_name: name,
      ts: new Date().toISOString(),
      page_load_id: pageLoadId,
      consent: "granted",
      consent_state: state,
      customer_state: cfg.cs || "anonymous",
      page_type: cfg.pt || "page",
      page_handle: cfg.pgh || null,
      product_handle: cfg.ph || null,
      device_class: deviceClass(window.innerWidth),
      viewport_w: Math.min(32767, window.innerWidth | 0),
      viewport_h: Math.min(32767, window.innerHeight | 0),
      grid_cols: gridCols(),
      sort: grid.sort,
      filters_active: grid.filters_active,
      filters: grid.filters,
      referrer_host: landingInfo.referrer_host,
    };
    ev[K_TV] = VERSION;
    ev[K_COLL] = cfg.ch || null;
    for (var k in fields) if (fields[k] !== undefined) ev[k] = fields[k];
    return ev;
  }

  /** Identifiers stamped at flush time under granted (spec §4: the session cookie is created then). */
  function identify(ev) {
    var vid = cookie(VID_COOKIE);
    ev.visitor_id = vid || null;
    ev.shopify_client_id = vid || null;
    ev.session_id = session ? session.id : null;
    ev.ga_client_id = gaClientId(cookie(GA_COOKIE));
    ev.attr_key = attrKey;
    ev.consent_marketing = marketing();
    return ev;
  }

  // ── consent ────────────────────────────────────────────────────────────
  function cb() {
    return window[cfg.cb || "Cookiebot"];
  }
  function marketing() {
    var c = cb();
    return c && c.consent ? !!c.consent.marketing : null;
  }
  function readConsent() {
    var c = cb();
    if (!c || !c.hasResponse) return "unknown";
    return c.consent && c.consent.statistics ? "granted" : "denied";
  }
  function onConsent() {
    var next = readConsent();
    if (next === "unknown") return;
    var wasUnknown = consent === "unknown";
    consent = next;
    if (next === "granted") {
      touchSession();
      computeAttrKey();
      if (wasUnknown) {
        var q = queue.splice(0);
        q.forEach(function (ev) {
          buffer.push(ev);
        });
        schedule();
      }
    } else if (wasUnknown) {
      sendDenied(queue.splice(0));
    }
  }

  // ── sending ────────────────────────────────────────────────────────────
  function post(events, branch) {
    if (!events.length) return;
    var body = JSON.stringify({ v: 1, consent_state: branch, events: events });
    var ok = false;
    try {
      if (navigator[SEND]) ok = navigator[SEND](cfg.url, new Blob([body], { type: "text/plain" }));
    } catch (e) {}
    if (!ok) {
      try {
        fetch(cfg.url, { method: "POST", headers: { "content-type": "text/plain" }, body: body, keepalive: true, credentials: "omit", mode: "cors" }).catch(function () {});
      } catch (e) {}
    }
  }
  function finalizeLive() {
    var now = Date.now();
    for (var id in live) {
      var l = live[id];
      l.ev.dwell_ms = Math.min(60000, now - l.since);
    }
  }
  function flush() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (consent !== "granted" || !buffer.length) return;
    finalizeLive();
    if (session) {
      session.last = Date.now();
      writeSession(session);
    }
    var events = buffer.splice(0).map(identify);
    batches(events).forEach(function (b) {
      post(b, "granted");
    });
  }
  function sendDenied(events) {
    finalizeLive();
    var out = [];
    events.forEach(function (ev) {
      var d = denied(ev);
      if (d) out.push(d);
    });
    batches(out).forEach(function (b) {
      post(b, "denied");
    });
  }
  function schedule() {
    if (buffer.length >= FLUSH_N) return flush();
    if (!timer) timer = setTimeout(flush, FLUSH_MS);
  }

  /** Every event passes here: queued while unknown, sent per branch afterwards. */
  function emit(name, fields) {
    var ev = envelope(name, fields, consent === "unknown" ? "unknown_at_capture" : consent);
    if (consent === "unknown") enqueue(queue, ev);
    else if (consent === "denied") {
      var d = denied(ev);
      if (d) {
        buffer.push(d);
        if (!timer) timer = setTimeout(flushDenied, FLUSH_MS);
        if (buffer.length >= FLUSH_N) flushDenied();
      }
    } else {
      buffer.push(ev);
      schedule();
    }
    return ev;
  }
  function flushDenied() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    finalizeLive();
    batches(buffer.splice(0)).forEach(function (b) {
      post(b, "denied");
    });
  }
  function onHide() {
    if (consent === "granted") flush();
    else if (consent === "denied") flushDenied();
    else {
      // No decision on this page: the anonymous subset leaves, the rest is dropped.
      sendDenied(queue.splice(0));
    }
  }

  // ── helpers over the DOM ───────────────────────────────────────────────
  function attr(el, name) {
    return el ? el.getAttribute("data-lv-" + name) : null;
  }
  function num(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  }
  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentNode;
    }
    return null;
  }
  function newTab(e) {
    return e.type === "auxclick" || !!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1);
  }
  function on(type, fn, opts) {
    document.addEventListener(type, fn, opts || false);
  }

  // ── tiles: geometry and payload ────────────────────────────────────────
  var geom = { w: 0, rows: null, map: {} };
  function geometry(tile) {
    var listEl = closest(tile, "#ProductsList") || tile.parentNode;
    if (geom.w !== window.innerWidth || geom.list !== listEl) {
      geom = { w: window.innerWidth, list: listEl, map: {} };
      var tops = {}, tiles = listEl.querySelectorAll("[data-lv-index]");
      var i, t;
      for (i = 0; i < tiles.length; i++) tops[Math.round(tiles[i].offsetTop)] = 1;
      var rows = Object.keys(tops).map(Number).sort(function (a, b) { return a - b; });
      var lefts = {};
      for (i = 0; i < tiles.length; i++) {
        t = tiles[i];
        var top = Math.round(t.offsetTop), left = Math.round(t.offsetLeft);
        (lefts[top] = lefts[top] || []).push(left);
      }
      for (i = 0; i < tiles.length; i++) {
        t = tiles[i];
        var tp = Math.round(t.offsetTop);
        var ls_ = lefts[tp].slice().sort(function (a, b) { return a - b; }).filter(function (v, k, a) { return a.indexOf(v) === k; });
        geom.map[attr(t, "index")] = { row: rows.indexOf(tp) + 1, col: ls_.indexOf(Math.round(t.offsetLeft)) + 1 };
      }
    }
    return geom.map[attr(tile, "index")] || { row: 1, col: 1 };
  }
  function imageIndex(tile) {
    var segs = tile.querySelectorAll(".product-card__carousel-indicator-segment");
    for (var i = 0; i < segs.length; i++) if (segs[i].classList.contains("is-active")) return i;
    return 0;
  }
  function position(tile) {
    var g = geometry(tile);
    return {
      product_handle: attr(tile, "handle"),
      position_index: num(attr(tile, "index")),
      position_cell: num(attr(tile, "cell")),
      position_span: num(attr(tile, "span")) || 1,
      position_row: g.row,
      position_col: g.col,
      tile_variant: attr(tile, "variant") || "tile",
      page_number: num(attr(tile, "page")) || 1,
      card_image_index: imageIndex(tile),
    };
  }
  function tileOf(el) {
    return closest(el, "[data-lv-index][data-lv-handle]");
  }

  // ── click store (granted only) ─────────────────────────────────────────
  function rememberClick(p) {
    var list = ls(LS_CLICKS) || [];
    list = list.filter(function (c) { return c.h !== p.product_handle; });
    list.push({ h: p.product_handle, c: cfg.ch || null, i: p.position_index, r: p.position_row, ts: Date.now() });
    ls(LS_CLICKS, list.slice(-20));
  }
  function recentClick(handle) {
    var list = ls(LS_CLICKS) || [];
    for (var i = list.length - 1; i >= 0; i--) if (list[i].h === handle && Date.now() - list[i].ts < 30 * 60000) return list[i];
    return null;
  }

  // ── cart intent attribute (granted only) ───────────────────────────────
  var intentTimer = null, intentSig = null;
  function intent(mutate) {
    if (consent !== "granted") return;
    var s = ss(SS_INTENT) || { v: 1, h: [], m: [], s: [], a: [], vc: 0 };
    mutate(s);
    s.h = s.h.slice(-3);
    ss(SS_INTENT, s);
    if (intentTimer) clearTimeout(intentTimer);
    intentTimer = setTimeout(function () {
      var body = JSON.stringify(s);
      var sig = hash36(body);
      if (sig === intentSig) return;
      intentSig = sig;
      try {
        fetch("/cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: { _lv_intent: body } }) }).catch(function () {});
      } catch (e) {}
    }, 400);
  }

  // ── page_view ──────────────────────────────────────────────────────────
  function pageView() {
    var landingGranted = session ? session.isNew : null;
    var landingAnon = !document.referrer || !isSelf(hostOf(document.referrer));
    emit("page_view", {
      landing: consent === "granted" ? !!landingGranted : landingAnon,
      source: landingInfo.source,
      medium: landingInfo.medium,
      campaign: landingInfo.campaign,
      landing_handle: cfg.pt === "index" ? "/" : cfg.pgh || null,
      has_gclid: landingInfo.has_gclid,
      has_fbclid: landingInfo.has_fbclid,
      has_epik: landingInfo.has_epik,
    });
  }

  // ── impressions and clicks ─────────────────────────────────────────────
  function observeTiles() {
    if (!("IntersectionObserver" in window)) return;
    var pending = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var tile = en.target;
        var key = pageLoadId + "|" + (cfg.ch || "") + "|" + attr(tile, "handle") + "|" + attr(tile, "index");
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (seenImpressions[key] || pending[key]) return;
          pending[key] = setTimeout(function () {
            delete pending[key];
            seenImpressions[key] = 1;
            var ev = emit("product_impression", Object.assign(position(tile), { dwell_ms: 500 }));
            live[ev.event_id] = { ev: ev, since: Date.now() - 500 };
            tile.__lvLive = ev.event_id;
          }, 500);
        } else {
          if (pending[key]) {
            clearTimeout(pending[key]);
            delete pending[key];
          }
          if (tile.__lvLive && live[tile.__lvLive]) {
            var l = live[tile.__lvLive];
            l.ev.dwell_ms = Math.min(60000, Date.now() - l.since);
            delete live[tile.__lvLive];
            tile.__lvLive = null;
          }
        }
      });
    }, { threshold: [0.5] });
    var tiles = document.querySelectorAll("[data-lv-index][data-lv-handle]");
    for (var i = 0; i < tiles.length; i++) io.observe(tiles[i]);
    // Tiles added by facet or infinite updates.
    new MutationObserver(function () {
      var all = document.querySelectorAll("[data-lv-index][data-lv-handle]");
      for (var j = 0; j < all.length; j++) if (!all[j].__lvObserved) {
        all[j].__lvObserved = 1;
        io.observe(all[j]);
      }
    }).observe(document.body, { childList: true, subtree: true });
    for (var k = 0; k < tiles.length; k++) tiles[k].__lvObserved = 1;
  }

  function onTileClick(e) {
    var a = closest(e.target, "a[href*='/products/'], [data-lv-quickview]");
    if (!a) return;
    var tile = tileOf(a);
    if (!tile) return;
    if (closest(e.target, "[data-carousel-prev], [data-carousel-next], .product-card__carousel-indicator")) return;
    var p = position(tile);
    emit("product_click", Object.assign(p, { new_tab: newTab(e), target: a.hasAttribute("data-lv-quickview") ? "quick_view" : "pdp" }));
    rememberClick(p);
  }

  function observeSwipes() {
    var last = {};
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var seg = m.target;
        if (!seg.classList || !seg.classList.contains("product-card__carousel-indicator-segment") || !seg.classList.contains("is-active")) return;
        var tile = tileOf(seg);
        if (!tile) return;
        var idx = attr(tile, "index");
        var now = Date.now();
        var to = imageIndex(tile);
        var from = last[idx] === undefined ? 0 : last[idx].i;
        if (last[idx] && now - last[idx].t < 300) { last[idx].i = to; return; }
        if (to === from) return;
        last[idx] = { i: to, t: now };
        var method = "auto";
        if (now - lastPointer.t < 1500) method = closest(lastPointer.el, "[data-carousel-prev], [data-carousel-next]") ? "arrow" : closest(lastPointer.el, ".product-card__carousel-indicator") ? "dot" : "swipe";
        emit("card_swipe", Object.assign(position(tile), { from_index: from, to_index: to, method: method }));
      });
    }).observe(document.body, { attributes: true, attributeFilter: ["class"], subtree: true });
  }

  // ── navigation and sections ────────────────────────────────────────────
  function onNavClick(e) {
    var a = closest(e.target, "[data-lv-nav]");
    if (!a) return;
    emit("nav_click", {
      element_id: attr(a, "nav"),
      nav_surface: attr(a, "nav-surface") || "header",
      nav_depth: num(attr(a, "nav-depth")) || 1,
      destination: pathOf(a.getAttribute("href") || ""),
      new_tab: newTab(e),
    });
  }
  function observeSections() {
    if (!("IntersectionObserver" in window)) return;
    var seen = {}, pending = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var el = en.target, id = attr(el, "section");
        if (en.isIntersecting && en.intersectionRatio >= 0.5) {
          if (seen[id] || pending[id]) return;
          pending[id] = setTimeout(function () {
            delete pending[id];
            seen[id] = 1;
            emit("section_view", { element_id: id, section_index: num(attr(el, "section-index")) || 0 });
          }, 500);
        } else if (pending[id]) {
          clearTimeout(pending[id]);
          delete pending[id];
        }
      });
    }, { threshold: [0.5] });
    var els = document.querySelectorAll("[data-lv-section]");
    for (var i = 0; i < els.length; i++) io.observe(els[i]);
  }
  function onSectionClick(e) {
    if (tileOf(e.target)) return;
    var cta = closest(e.target, "[data-lv-cta]") || (cfg.pt === "index" ? closest(e.target, "a[href], button") : null);
    if (!cta) return;
    var section = closest(cta, "[data-lv-section]");
    if (!section) return;
    var href = cta.getAttribute && cta.getAttribute("href");
    emit("section_click", {
      element_id: attr(section, "section"),
      cta_id: attr(cta, "cta") || (href ? pathOf(href) : cap(cta.textContent.trim(), 40) || "button"),
      destination: href ? pathOf(href) : "",
      new_tab: newTab(e),
    });
  }

  // ── facets, sort, search ───────────────────────────────────────────────
  var facetTimer = null;
  function resultCount() {
    var c = document.getElementById("ProductGridContainer");
    return c ? num(attr(c, "results")) || 0 : 0;
  }
  function onFacetChange(e) {
    if (!closest(e.target, "[data-lv-facets]")) return;
    if (closest(e.target, "[data-lv-sort]")) return;
    if (facetTimer) clearTimeout(facetTimer);
    facetTimer = setTimeout(function () {
      grid = gridState(location.search, cfg.sort_default);
      emit("filter_apply", { result_count: resultCount() });
    }, 400);
  }
  function onSortChange(e) {
    // The theme's sort is a custom list ([data-lv-sort] [data-value]); a native select is handled too.
    var root_ = closest(e.target, "[data-lv-sort]");
    if (!root_) return;
    var item = closest(e.target, "[data-value]");
    var value = e.type === "change" ? e.target.value : item ? item.getAttribute("data-value") : null;
    if (!value || value === grid.sort) return;
    grid.sort = value;
    setTimeout(function () {
      emit("sort_change", { result_count: resultCount() });
    }, 600);
  }
  var searchTimer = null, overlayCount = 0;
  function submitSearch(raw, source, count) {
    var norm = normQuery(raw);
    if (!norm) return;
    var done = ss(SS_Q) || {};
    if (consent === "granted" && done[norm]) return;
    done[norm] = 1;
    ss(SS_Q, done);
    emit("search_submit", { query_norm: norm, query_raw: guard(raw), norm_version: 1, result_count: Math.max(0, count | 0), source: source });
  }
  function onSearchInput(e) {
    var el = closest(e.target, "[data-lv-search]");
    if (!el || attr(el, "search") !== "overlay") return;
    var raw = el.value || "";
    if (searchTimer) clearTimeout(searchTimer);
    if (raw.trim().length < 3) return;
    searchTimer = setTimeout(function () {
      overlayCount = document.querySelectorAll("[data-pso-rendered-products] .pso__rendered-card, [data-pso-products] .pso__rendered-card").length;
      submitSearch(raw, "overlay", overlayCount);
    }, 800);
  }
  function onSearchSubmit(e) {
    var input = e.target && e.target.querySelector ? e.target.querySelector("[data-lv-search]") : null;
    if (!input) return;
    if (searchTimer) clearTimeout(searchTimer);
    submitSearch(input.value || "", "enter", overlayCount);
  }
  function onOverlayEnter(e) {
    if (e.key !== "Enter") return;
    var el = closest(e.target, "[data-lv-search]");
    if (!el || attr(el, "search") !== "overlay") return;
    if (searchTimer) clearTimeout(searchTimer);
    submitSearch(el.value || "", "enter", overlayCount);
  }
  function onResultClick(e) {
    var item = closest(e.target, "[data-lv-result-type]");
    if (!item) return;
    var onResultsPage = !!closest(item, "[data-lv-search-page]");
    var q = onResultsPage ? cfg.search_terms || "" : (document.querySelector("[data-lv-search='overlay'], [data-lv-search='enter']") || {}).value || "";
    if (onResultsPage) {
      emit("search_result_click", { query_norm: normQuery(q), product_handle: attr(item, "handle"), position: num(attr(item, "position")) || 1, new_tab: newTab(e) });
    } else {
      emit("search_suggestion_click", { query_norm: normQuery(q), type: attr(item, "result-type") || "product", position: num(attr(item, "position")) || 1, target_handle: attr(item, "handle"), new_tab: newTab(e) });
    }
  }

  // ── PDP ────────────────────────────────────────────────────────────────
  function sinceView() {
    return Date.now() - t0;
  }
  function pdpView() {
    var v = cfg.variant || {};
    var click = recentClick(cfg.ph);
    var refPath = isSelf(hostOf(document.referrer)) ? pathOf(document.referrer) : null;
    var arrived = click ? ARR_COLL : refPath === null ? (document.referrer ? "other" : "direct") : refPath.indexOf(COLL_PATH) === 0 ? ARR_COLL : refPath.indexOf("/search") === 0 ? "search" : "nav";
    var seen = ls(LS_SEEN) || {};
    var prev = seen[cfg.ph] || null;
    var fields = {
      variant_id: String(v.id || ""),
      price: Number(v.price || 0),
      in_stock: !!v.available,
      arrived_from: arrived,
      entry_position: click ? click.r + ":" + click.i : null,
      repeat_view_count: prev ? prev.n : 0,
      repeat_view_days: prev ? Math.floor((Date.now() - prev.last) / 86400000) : null,
    };
    if (consent === "granted") {
      seen[cfg.ph] = { n: (prev ? prev.n : 0) + 1, last: Date.now() };
      ls(LS_SEEN, seen);
      pdp.views = 1;
      intent(function (s) {
        if (s.h.indexOf(cfg.ph) === -1) s.h.push(cfg.ph);
        s.pv = (s.pv || 0) + 1;
      });
    }
    emit("pdp_view", fields);
  }
  function observeGallery() {
    var g = document.querySelector("[data-lv-gallery]");
    if (!g) return;
    var last = 0, lastT = 0, count = 0;
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        var el = m.target;
        if (!el.classList || !el.hasAttribute("data-lv-gallery-index") || !el.classList.contains("swiper-slide-active")) return;
        var to = num(attr(el, "gallery-index")) || 0;
        var now = Date.now();
        if (to === last || now - lastT < 300 || count >= 40) return;
        var method = "auto";
        if (now - lastPointer.t < 1500) method = closest(lastPointer.el, ".product__media-gallery-thumbails") ? "thumbnail" : closest(lastPointer.el, ".swiper-button-prev, .swiper-button-next") ? "arrow" : "swipe";
        emit("pdp_gallery_swipe", { from_index: last, to_index: to, method: method });
        pdp.images[to] = 1;
        pdp.gallery += 1;
        count += 1;
        last = to;
        lastT = now;
      });
    }).observe(g, { attributes: true, attributeFilter: ["class"], subtree: true });
  }
  var lastVariant = null;
  function onVariantSelect(e) {
    var opt = closest(e.target, "[data-lv-variant-picker] [data-option]");
    var sel = !opt && e.type === "change" ? closest(e.target, "[data-lv-variant-picker] select") : null;
    if (!opt && !sel) return;
    setTimeout(function () {
      var input = document.querySelector("form[data-lv-atc='pdp'] [name='id'], .product-variant-id");
      var vid = input ? String(input.value) : String((cfg.variant || {}).id || "");
      emit("pdp_variant_select", {
        variant_id: vid,
        from_variant_id: lastVariant || String((cfg.variant || {}).id || ""),
        option_name: opt ? opt.getAttribute("data-option") || "" : sel.getAttribute("name") || "option",
        option_value: opt ? opt.getAttribute("data-value") || "" : sel.value || "",
      });
      lastVariant = vid;
      pdp.variants += 1;
      if (pdp.variants === 3) intent(function (s) { s.vc = pdp.variants; });
    }, 50);
  }
  function onAccordion(e) {
    var d = e.target;
    if (!d || !d.hasAttribute || !d.hasAttribute("data-lv-accordion") || !d.open) return;
    var id = attr(d, "accordion");
    if (pdp.accordions[id]) return;
    pdp.accordions[id] = 1;
    emit("pdp_accordion_open", { accordion_id: id, ms_since_view: sinceView() });
    intent(function (s) { if (s.a.indexOf(id) === -1) s.a.push(id); });
  }
  function onScroll() {
    var h = document.documentElement;
    var max = h.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    var pct = Math.round(((window.scrollY || h.scrollTop) / max) * 100);
    [25, 50, 75, 100].forEach(function (d) {
      if (pct >= d && !pdp.depths[d]) {
        pdp.depths[d] = 1;
        pdp.scroll = Math.max(pdp.scroll, d);
        emit("pdp_scroll_depth", { depth_pct: d, ms_since_view: sinceView() });
      }
    });
  }
  var atcTimes = {};
  function onAtc(e) {
    var form = closest(e.target, "form[data-lv-atc]");
    if (!form) return;
    var source = attr(form, "atc") || "pdp";
    if (closest(form, "quick-view-modal")) source = "quick_view";
    var idEl = form.querySelector("[name='id']"), qEl = form.querySelector("[name='quantity']");
    var tile = tileOf(form);
    var handle = tile ? attr(tile, "handle") : cfg.ph || attr(closest(form, "[data-lv-handle]"), "handle");
    var fields = {
      product_handle: handle,
      atc_source: source,
      variant_id: idEl ? String(idEl.value) : "",
      quantity: qEl ? Math.max(1, num(qEl.value) || 1) : 1,
      ms_since_view: source === "pdp" && cfg.pt === "product" ? sinceView() : null,
      gallery_swipes: source === "pdp" ? pdp.gallery : null,
      variant_changes: source === "pdp" ? pdp.variants : null,
      max_scroll_pct: source === "pdp" ? pdp.scroll : null,
      position_index: tile ? num(attr(tile, "index")) : null,
      position_row: tile ? geometry(tile).row : null,
    };
    atcTimes[handle] = Date.now();
    emit("pdp_atc", fields);
  }
  function onSampleClick(e) {
    var el = closest(e.target, "[data-lv-sample]");
    if (!el) return;
    var sku = attr(el, "sample"), material = attr(el, "material");
    emit("pdp_sample_click", { sample_sku: sku || "", variant_id: String((cfg.variant || {}).id || "") || null, material: material || null });
    intent(function (s) { if (sku && s.s.indexOf(sku) === -1) s.s.push(sku); if (material && s.m.indexOf(material) === -1) s.m.push(material); });
  }

  // ── cart ───────────────────────────────────────────────────────────────
  var lastCartState = null;
  function cartRoot() {
    return document.querySelector("cart-drawer[open] [data-lv-cart], cart-drawer[open][data-lv-cart], [data-lv-cart]:not(cart-drawer *)") || document.querySelector("[data-lv-cart]");
  }
  function cartSnapshot(root_) {
    var lines = root_.querySelectorAll("[data-lv-cart-line]");
    var handles = [], items = 0;
    for (var i = 0; i < lines.length; i++) {
      var l = lines[i];
      handles.push(attr(l, "cart-line") + (l.classList.contains("cart-sample") ? "~" : ""));
      items += num(l.getAttribute("data-quantity")) || 1;
    }
    var valueEl = root_.querySelector("[data-lv-cart-value]") || closest(root_, "[data-lv-cart-value]");
    var bandEl = root_.querySelector("[data-lv-shipping-band]");
    return {
      line_handles: handles,
      line_count: lines.length,
      item_count: items,
      cart_value: valueEl ? (num(attr(valueEl, "cart-value")) || 0) / 100 : 0,
      shipping_band: bandEl ? attr(bandEl, "shipping-band") : null,
      shipping_method_shown: null,
    };
  }
  function cartView(reason) {
    var root_ = cartRoot();
    if (!root_) return;
    var snap = cartSnapshot(root_);
    var state = hash36(snap.line_handles.join(",") + "|" + snap.item_count + "|" + snap.cart_value);
    if (state === lastCartState) return;
    lastCartState = state;
    var ev = emit("cart_view", Object.assign(snap, { cart_token_hash: "" }));
    try {
      fetch("/cart.js", { credentials: "same-origin" }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) {
        if (c && c.token) sha256(c.token).then(function (h) { if (h) ev.cart_token_hash = h; });
      }).catch(function () {});
    } catch (e) {}
    void reason;
  }
  function observeCart() {
    var drawer = document.querySelector("cart-drawer");
    if (drawer) {
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          if (m.attributeName === "open" && drawer.hasAttribute("open")) setTimeout(function () { cartView("open"); }, 150);
        });
      }).observe(drawer, { attributes: true, attributeFilter: ["open"] });
    }
    var rerender = function () { setTimeout(function () { if ((drawer && drawer.hasAttribute("open")) || cfg.pt === "cart") cartView("update"); }, 400); };
    on("cart:updated", rerender);
    on("cart:refresh", rerender);
    if (cfg.pt === "cart") cartView("page");
  }
  function onRemove(e) {
    var a = closest(e.target, "a[is='cart-remove-item'][data-lv-handle]");
    if (!a) return;
    var root_ = cartRoot();
    var snap = root_ ? cartSnapshot(root_) : { cart_value: 0, shipping_band: null };
    var handle = attr(a, "handle");
    emit("cart_line_remove", {
      product_handle: handle,
      variant_id: attr(a, "variant-id"),
      ms_since_atc: atcTimes[handle] ? Date.now() - atcTimes[handle] : null,
      cart_value_before: snap.cart_value,
      shipping_band: snap.shipping_band,
    });
  }

  // ── boot ───────────────────────────────────────────────────────────────
  function boot() {
    consent = readConsent();
    if (consent === "granted") {
      touchSession();
      computeAttrKey();
    }
    window.addEventListener("CookiebotOnConsentReady", onConsent);
    window.addEventListener("CookiebotOnAccept", onConsent);
    window.addEventListener("CookiebotOnDecline", onConsent);
    on("pointerdown", function (e) { lastPointer = { t: Date.now(), el: e.target }; }, true);

    pageView();
    if (cfg.pt === "product" && cfg.ph) {
      pdpView();
      observeGallery();
      on("scroll", onScroll, { passive: true });
      on("toggle", onAccordion, true);
    }
    if (cfg.pt === "search" && cfg.search_terms) submitSearch(cfg.search_terms, "results", cfg.search_count | 0);
    observeTiles();
    observeSwipes();
    observeSections();
    observeCart();

    on("click", onTileClick, true);
    on("auxclick", onTileClick, true);
    on("click", onNavClick, true);
    on("auxclick", onNavClick, true);
    on("click", onSectionClick, true);
    on("click", onResultClick, true);
    on("auxclick", onResultClick, true);
    on("click", onVariantSelect, true);
    on("change", onVariantSelect, true);
    on("change", onFacetChange, true);
    on("change", onSortChange, true);
    on("click", onSortChange, true);
    on("input", onSearchInput, true);
    on("keydown", onOverlayEnter, true);
    on("submit", onSearchSubmit, true);
    on("submit", onAtc, true);
    on("click", onSampleClick, true);
    on("click", onRemove, true);

    on("visibilitychange", function () { if (document.visibilityState === "hidden") onHide(); });
    window.addEventListener("pagehide", onHide);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})(typeof globalThis !== "undefined" ? globalThis : this);
